import React from "react";
import { useSelectionStore } from "../../../stores/selectionStore";
import {
  clearSelectedVisualMeshes,
  setSelectedVisualMeshIds,
} from "../../../visual-mesh-multi-selection";

export function getSceneInventoryRangeTargetIds(targets, anchorId, targetId) {
  if (!Array.isArray(targets) || !targetId) return [];

  const targetIndex = targets.findIndex((target) => target?.id === targetId);
  if (targetIndex < 0) return [];

  const anchorIndex = targets.findIndex((target) => target?.id === anchorId);
  if (anchorIndex < 0) return [targetId];

  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  return targets.slice(start, end + 1).map((target) => target.id);
}

export function applySceneInventoryRangeSelection(currentIds, rangeIds, shouldAddToSelection) {
  const next = shouldAddToSelection ? new Set(currentIds) : new Set();
  rangeIds.forEach((id) => {
    if (id) next.add(id);
  });
  return next;
}

export function useSceneInventorySelection({
  actuators,
  selectedMeshIds,
  selectedTargets,
  toggleBodyInSelection,
}) {
  const multiSelectAnchorRef = React.useRef({});

  const setBodySelection = React.useCallback((bodyIds) => {
    clearSelectedVisualMeshes();
    const selection = useSelectionStore.getState();
    selection.setLastSelectionSource("sceneTree");
    selection.setSelectedBodies([...bodyIds]);
  }, []);

  const setMeshSelection = React.useCallback((meshIds) => {
    const selection = useSelectionStore.getState();
    selection.setLastSelectionSource("sceneTree");
    setSelectedVisualMeshIds(meshIds);
    if (meshIds.size === 1) {
      selection.clearAllSelections();
      selection.setLastSelectionSource("sceneTree");
      selection.selectMesh([...meshIds][0]);
    } else {
      selection.clearAllSelections();
    }
  }, []);

  const clearMultiSelection = React.useCallback(
    (rowKey) => {
      multiSelectAnchorRef.current[rowKey] = null;
      if (rowKey === "body") {
        setBodySelection(new Set());
        return;
      }
      if (rowKey === "mesh") {
        setMeshSelection(new Set());
      }
    },
    [setBodySelection, setMeshSelection]
  );

  const selectAllTargets = React.useCallback(
    (rowKey, targets = []) => {
      const targetIds = targets.map((target) => target.id).filter(Boolean);
      multiSelectAnchorRef.current[rowKey] = targetIds[0] || null;
      if (rowKey === "body") {
        setBodySelection(new Set(targetIds));
        return;
      }
      if (rowKey === "mesh") {
        setMeshSelection(new Set(targetIds));
      }
    },
    [setBodySelection, setMeshSelection]
  );

  const openTarget = React.useCallback(
    (panelType, target) => {
      if (!target) return;

      const selection = useSelectionStore.getState();
      selection.clearAllSelections();
      clearSelectedVisualMeshes();

      switch (panelType) {
        case "object":
          selection.setLastSelectionSource("sceneTreeObject");
          selection.selectBody(target.id);
          break;
        case "body":
          selection.setLastSelectionSource("sceneTree");
          selection.selectBody(target.id);
          break;
        case "joint":
          selection.setLastSelectionSource("sceneTree");
          selection.selectJoint(target.id);
          break;
        case "actuator": {
          const actuator = actuators?.[target.id];
          selection.setLastSelectionSource("sceneTree");
          selection.selectActuatorInParent(
            target.id,
            actuator?.joint ?? null,
            actuator?.tendonUuid ?? null
          );
          break;
        }
        case "mesh":
          selection.setLastSelectionSource("sceneTree");
          selection.selectMesh(target.id);
          break;
        case "site":
          selection.setLastSelectionSource("sceneTree");
          selection.selectSite(target.id);
          break;
        case "camera":
          selection.setLastSelectionSource("sceneTree");
          selection.selectCamera(target.id);
          break;
        case "collision":
        case "visual":
          selection.setLastSelectionSource("scene");
          selection.selectGeom(target.id);
          break;
        case "inertial":
          selection.setLastSelectionSource("sceneTree");
          selection.selectInertial(target.id);
          break;
        case "constraint":
          selection.setLastSelectionSource("sceneTree");
          selection.selectEquality(target.id);
          break;
        case "tendon":
          selection.setLastSelectionSource("sceneTree");
          selection.selectTendon(target.id);
          break;
        default:
          break;
      }
    },
    [actuators]
  );

  const getActiveTargetId = React.useCallback(
    (panelType) => {
      switch (panelType) {
        case "object":
        case "body":
          return selectedTargets.body;
        case "joint":
          return selectedTargets.joint;
        case "actuator":
          return selectedTargets.actuator;
        case "mesh":
          return selectedTargets.mesh;
        case "site":
          return selectedTargets.site;
        case "collision":
        case "visual":
          return selectedTargets.geom;
        case "inertial":
          return selectedTargets.inertial;
        case "constraint":
          return selectedTargets.equality;
        case "tendon":
          return selectedTargets.tendon;
        case "camera":
          return selectedTargets.camera;
        default:
          return null;
      }
    },
    [selectedTargets]
  );

  const handleTargetRowKeyDown = React.useCallback(
    (panelType, targets, index, event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        String(event.key || "").toLowerCase() === "a" &&
        (panelType === "body" || panelType === "mesh")
      ) {
        event.preventDefault();
        const targetIds = targets.map((target) => target.id);
        multiSelectAnchorRef.current[panelType] = targetIds[index] || targetIds[0] || null;
        if (panelType === "body") {
          setBodySelection(new Set(targetIds));
        } else {
          setMeshSelection(new Set(targetIds));
        }
        return;
      }

      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();

      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = Math.max(0, Math.min(targets.length - 1, index + direction));
      if (nextIndex === index) return;

      const currentItem = event.currentTarget;
      openTarget(panelType, targets[nextIndex]);
      requestAnimationFrame(() => {
        const list = currentItem.closest("[data-scene-inventory-list='true']");
        const buttons = list?.querySelectorAll("[data-scene-inventory-item='true']");
        buttons?.[nextIndex]?.focus();
      });
    },
    [openTarget, setBodySelection, setMeshSelection]
  );

  const toggleMultiSelection = React.useCallback(
    (rowKey, targetId, targets = [], event = null) => {
      const isRangeSelection = !!event?.shiftKey;
      const shouldAddRange = !!(event?.ctrlKey || event?.metaKey);

      if (rowKey === "body") {
        const selection = useSelectionStore.getState();
        if (isRangeSelection) {
          const rangeIds = getSceneInventoryRangeTargetIds(
            targets,
            multiSelectAnchorRef.current[rowKey],
            targetId
          );
          const nextSelection = applySceneInventoryRangeSelection(
            selection.selectedBodies,
            rangeIds,
            shouldAddRange
          );
          setBodySelection(nextSelection);
        } else {
          clearSelectedVisualMeshes();
          selection.setLastSelectionSource("sceneTree");
          toggleBodyInSelection(targetId);
        }
        multiSelectAnchorRef.current[rowKey] = targetId;
        return;
      }

      if (rowKey === "mesh") {
        const selection = useSelectionStore.getState();
        selection.setLastSelectionSource("sceneTree");

        if (isRangeSelection) {
          const rangeIds = getSceneInventoryRangeTargetIds(
            targets,
            multiSelectAnchorRef.current[rowKey],
            targetId
          );
          const nextSelection = applySceneInventoryRangeSelection(
            selectedMeshIds,
            rangeIds,
            shouldAddRange
          );
          setMeshSelection(nextSelection);
        } else {
          const nextSelection = new Set(selectedMeshIds);
          if (nextSelection.has(targetId)) {
            nextSelection.delete(targetId);
          } else {
            nextSelection.add(targetId);
          }
          setMeshSelection(nextSelection);
        }
        multiSelectAnchorRef.current[rowKey] = targetId;
      }
    },
    [selectedMeshIds, setBodySelection, setMeshSelection, toggleBodyInSelection]
  );

  return {
    clearMultiSelection,
    getActiveTargetId,
    handleTargetRowKeyDown,
    openTarget,
    selectAllTargets,
    toggleMultiSelection,
  };
}
